import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { MediabilityLogo } from "@/components/Logo";
import { cn } from "@/lib/utils";

const EXAMPLE = "We want a small studio at our head office so our communications team can run town halls and record short video updates without a technician. It should work with Microsoft Teams.";

export default function PublicDiscoveryPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");
  const [contact, setContact] = useState({ name: "", company: "", email: "" });
  const [question, setQuestion] = useState(null);
  const [qa, setQa] = useState([]);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [company, setCompany] = useState({});

  useEffect(() => {
    document.title = "Tell us about your project - Mediability";
    api.get(`/public/discovery/${token}`).then((r) => {
      setSession(r.data);
      setCompany(r.data.company || {});
      setQa(r.data.qa || []);
      setQuestion(r.data.current_question || null);
      setText(r.data.initial_text || "");
      setContact({ name: r.data.contact?.name || "", company: r.data.contact?.company || "", email: r.data.contact?.email || "" });
      if (r.data.result) setResult(r.data.result);
    }).catch((e) => setError(errMsg(e)));
  }, [token]);

  const start = async (e) => {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error("Tell us a little more about what you want to achieve.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/public/discovery/${token}/start`, { text, ...contact });
      setQa([]);
      setQuestion(r.data.question);
      setSession({ ...session, status: "in_progress" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/public/discovery/${token}/finish`);
      setResult(r.data);
      setSession({ ...session, status: "completed" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitAnswer = async (val) => {
    const a = (val ?? answer).trim();
    if (!a) return;
    setBusy(true);
    try {
      const r = await api.post(`/public/discovery/${token}/answer`, { answer: a });
      setQa(r.data.qa);
      setAnswer("");
      if (r.data.question?.done) {
        setQuestion(r.data.question);
        await finish();
      } else {
        setQuestion(r.data.question);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post(`/public/discovery/${token}/reset`);
      setResult(null);
      setQa([]);
      setQuestion(null);
      setText("");
      setAnswer("");
      setContact({ name: "", company: "", email: "" });
      setSession({ ...session, status: "new" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const stage = result ? "done" : session?.status === "in_progress" && question ? "questions" : busy && session?.status === "in_progress" ? "finishing" : "intro";
  const progress = stage === "done" ? 100 : stage === "questions" ? Math.max(15, Math.round((question?.progress || qa.length / 6) * 100)) : 5;

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[hsl(220_14%_96%)] p-6">
        <div className="max-w-md rounded-2xl border bg-white p-8 text-center shadow-float"><MediabilityLogo className="mx-auto h-8" /><h1 className="mt-6 text-xl font-semibold">This link is not valid</h1><p className="mt-2 text-sm text-muted-foreground">{error}. Please ask your Mediability contact for a new link.</p></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[hsl(220_14%_96%)]" data-testid="public-discovery-page">
      <header className="border-b bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4 sm:px-6"><MediabilityLogo className="h-8" testId="public-discovery-logo" /><span className="text-xs text-muted-foreground">{company.tagline || "Content | Engineered"}</span></div>
        <div className="mx-auto max-w-3xl px-4 pb-3 sm:px-6"><Progress value={progress} className="h-1" data-testid="public-discovery-progress" /></div>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-4 py-10 sm:px-6">
        {!session ? <div className="text-sm text-muted-foreground">Loading...</div> : (
          <AnimatePresence mode="wait">
            {stage === "intro" && (
              <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.28 }}>
                <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">New project</div>
                <h1 className="mt-3 text-3xl font-semibold leading-[1.1] tracking-[-0.02em] sm:text-5xl" style={{ fontFamily: "var(--font-display)" }}>Tell us about your project.</h1>
                <p className="mt-4 max-w-xl text-base text-muted-foreground">Describe what you want to achieve in your own words. Our assistant asks a few follow-up questions and a Mediability specialist gets back to you with a first proposal.</p>
                <form onSubmit={start} className="mt-8 space-y-4 rounded-2xl border bg-white p-5 shadow-float sm:p-6" data-testid="public-discovery-intro-form">
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder={EXAMPLE} className="rounded-xl text-base leading-7" autoFocus data-testid="public-discovery-text-input" />
                  <button type="button" onClick={() => setText(EXAMPLE)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="public-discovery-use-example-button">Use an example</button>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" className="h-10 rounded-[10px]" data-testid="public-discovery-name-input" />
                    <Input value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} placeholder="Company" className="h-10 rounded-[10px]" data-testid="public-discovery-company-input" />
                    <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="E-mail" className="h-10 rounded-[10px]" data-testid="public-discovery-email-input" />
                  </div>
                  <div className="flex items-center justify-between gap-3"><span className="text-xs text-muted-foreground">Takes about two minutes. No account needed.</span><Button type="submit" disabled={busy} className="h-10 gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="public-discovery-start-button">{busy ? "Thinking..." : "Continue"} <ArrowRight className="h-4 w-4" /></Button></div>
                </form>
              </motion.div>
            )}

            {stage === "questions" && question && (
              <motion.div key={`q-${qa.length}`} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.26 }} data-testid="public-discovery-question">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-primary"><Sparkles className="h-3.5 w-3.5" /> Question {qa.length + 1}</div>
                <h2 className="mt-3 text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-4xl" style={{ fontFamily: "var(--font-display)" }} data-testid="public-discovery-question-text">{question.question}</h2>
                {question.why && <p className="mt-3 max-w-xl text-sm text-muted-foreground">{question.why}</p>}
                <div className="mt-8 rounded-2xl border bg-white p-5 shadow-float sm:p-6">
                  {question.type === "choice" && question.options?.length ? (
                    <div className="flex flex-wrap gap-2" data-testid="public-discovery-options">
                      {question.options.map((opt) => <button key={opt} type="button" disabled={busy} onClick={() => submitAnswer(opt)} className={cn("rounded-full border bg-white px-4 py-2 text-sm font-medium hover:border-foreground/40 hover:bg-muted/50 disabled:opacity-50")} data-testid="public-discovery-option-button">{opt}</button>)}
                      <div className="mt-2 flex w-full gap-2"><Input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Or write your own answer" className="h-10 rounded-[10px]" onKeyDown={(e) => e.key === "Enter" && submitAnswer()} data-testid="public-discovery-answer-input" /><Button onClick={() => submitAnswer()} disabled={busy || !answer.trim()} className="h-10 bg-primary text-white hover:bg-[#ff8e76]" data-testid="public-discovery-answer-submit-button"><ArrowRight className="h-4 w-4" /></Button></div>
                    </div>
                  ) : (
                    <form onSubmit={(e) => { e.preventDefault(); submitAnswer(); }} className="flex flex-col gap-3 sm:flex-row">
                      {question.type === "number" ? <Input type="number" value={answer} onChange={(e) => setAnswer(e.target.value)} className="h-11 rounded-[10px] text-base" autoFocus data-testid="public-discovery-answer-input" /> : <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} className="rounded-xl text-base leading-7" autoFocus onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitAnswer(); }} data-testid="public-discovery-answer-input" />}
                      <Button type="submit" disabled={busy || !answer.trim()} className="h-11 gap-2 bg-primary text-white hover:bg-[#ff8e76] sm:self-end" data-testid="public-discovery-answer-submit-button">{busy ? "Thinking..." : "Next"} <ArrowRight className="h-4 w-4" /></Button>
                    </form>
                  )}
                  <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground"><span>{qa.length} answered</span><button type="button" onClick={finish} disabled={busy || qa.length < 2} className="hover:text-foreground disabled:opacity-40" data-testid="public-discovery-finish-button">I'm done - send what I have</button></div>
                </div>
                {qa.length > 0 && <ul className="mt-6 space-y-1.5 text-sm text-muted-foreground">{qa.slice(-3).map((x, i) => <li key={i} className="flex gap-2"><Check className="mt-1 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-ok))]" /><span><span className="text-foreground/70">{x.question}</span> — {x.answer}</span></li>)}</ul>}
              </motion.div>
            )}

            {stage === "finishing" && (
              <motion.div key="finishing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkles className="h-6 w-6 animate-pulse" /></span>
                <h2 className="mt-5 text-2xl font-semibold">Putting your project together</h2>
                <p className="mt-1 text-sm text-muted-foreground">Structuring your needs so our team can respond precisely.</p>
              </motion.div>
            )}

            {stage === "done" && result && (
              <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} data-testid="public-discovery-result">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--status-ok))] text-white"><Check className="h-6 w-6" /></span>
                <h1 className="mt-6 text-3xl font-semibold leading-[1.1] tracking-[-0.02em] sm:text-5xl" style={{ fontFamily: "var(--font-display)" }}>Thank you{contact.name ? `, ${contact.name.split(" ")[0]}` : ""}.</h1>
                <p className="mt-4 max-w-xl text-base text-muted-foreground">We have structured your project and a Mediability specialist will get back to you within one business day with a first proposal.</p>
                <div className="mt-8 rounded-2xl border bg-white p-5 shadow-float sm:p-6">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What we understood</div>
                  <div className="mt-2 text-lg font-medium" data-testid="public-discovery-result-title">{result.opportunity_name}</div>
                  <p className="mt-2 text-sm leading-6 text-foreground/90">{result.summary}</p>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground"><span><span className="font-semibold tabular text-foreground">{result.requirements_count}</span> requirements captured</span><span>for <span className="font-medium text-foreground">{result.account_name}</span></span></div>
                </div>
                <div className="mt-6 flex items-center gap-3 text-xs text-muted-foreground"><span>{company.email || "sales@mediability.com"}{company.phone ? ` · ${company.phone}` : ""}</span><button type="button" onClick={reset} disabled={busy} className="ml-auto inline-flex items-center gap-1 hover:text-foreground" data-testid="public-discovery-reset-button"><RotateCcw className="h-3 w-3" /> Start over</button></div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>
      <footer className="py-6 text-center text-xs text-muted-foreground">{company.name || "Mediability"} · Independent systems integrator · Norway, Sweden, Denmark, MENA</footer>
    </div>
  );
}
